/**
 * Light/dark scheme preference, layered on top of the brand token system
 * (see styles.css :root / [data-theme='dark'] and brand.ts's applyBrandTheme).
 * 'system' (the default) tracks prefers-color-scheme via CSS alone — no JS
 * needed for that case. An explicit 'light'/'dark' choice is persisted and
 * reflected as a `data-theme` attribute on <html>, which the stylesheet's
 * dark-scheme block matches directly. index.html carries a tiny inline
 * script that reads the same storage key before first paint, so a returning
 * user's explicit choice never flashes the wrong scheme.
 */
import { useEffect, useState } from 'react';

export type ThemePreference = 'light' | 'dark' | 'system';

const THEME_KEY = 'aab_theme';
const THEME_CHANGE_EVENT = 'aab-theme-change';

export function getThemePreference(): ThemePreference {
  try {
    const stored = localStorage.getItem(THEME_KEY);
    if (stored === 'light' || stored === 'dark') return stored;
  } catch {
    /* storage disabled — fall back to system */
  }
  return 'system';
}

function applyTheme(pref: ThemePreference) {
  const root = document.documentElement;
  if (pref === 'system') root.removeAttribute('data-theme');
  else root.setAttribute('data-theme', pref);
}

export function setThemePreference(pref: ThemePreference) {
  try {
    if (pref === 'system') localStorage.removeItem(THEME_KEY);
    else localStorage.setItem(THEME_KEY, pref);
  } catch {
    /* storage disabled / full — preference just won't survive a reload */
  }
  applyTheme(pref);
  window.dispatchEvent(new Event(THEME_CHANGE_EVENT));
}

/** The scheme actually in effect right now (resolves 'system' via the OS). */
export function getResolvedScheme(): 'light' | 'dark' {
  const pref = getThemePreference();
  if (pref !== 'system') return pref;
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

/**
 * Live-updating resolved scheme — for anything that can't just read CSS vars
 * and needs the value in JS (e.g. the `theme` prop form-engine-portable
 * expects; the engine has no CSS-var/data-theme contract of its own and
 * never inspects the host's <html>, so it has to be told explicitly). Tracks
 * both an explicit in-app toggle (setThemePreference, same tab) and an OS
 * scheme change while 'system' is selected.
 */
export function useResolvedScheme(): 'light' | 'dark' {
  const [scheme, setScheme] = useState(getResolvedScheme);
  useEffect(() => {
    const recompute = () => setScheme(getResolvedScheme());
    const media = window.matchMedia('(prefers-color-scheme: dark)');
    window.addEventListener(THEME_CHANGE_EVENT, recompute);
    media.addEventListener('change', recompute);
    return () => {
      window.removeEventListener(THEME_CHANGE_EVENT, recompute);
      media.removeEventListener('change', recompute);
    };
  }, []);
  return scheme;
}
